import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Img from '../../Img';
import { getApiAxiosInstance } from '../../../utils/network';
import { showErrorPopup } from '../../../actions/notification-actions';
import { useCPPDynamicConfig } from '../../hooks/useCPPDynamicConfig';
import useCollegeProspectAccess from '../../hooks/useCollegeProspectAccess';
import { SOCIAL_PLATFORMS } from '../CollegeProspectTab/CollegeSocial/constants';
import { CppRecruitingStatuses, STATUS_LABELS } from '../CollegeProspectTab/CollegeRecruitmentStatus/constants';
import styles from './collegeProspectInfo.scss';

const TESTID_PREFIX = 'profile.student-info.college-prospect';
const HTTP_STATUS_FORBIDDEN = 403;
const EMPTY_VALUE_TEXT = 'NA';

const playerRecruitingStatusEndpoint = (memberId) => `/v1/cpp/player/${memberId}/recruiting-status`;
const playerSocialMediaEndpoint = (memberId) => `/v1/cpp/player/${memberId}/social-media`;

const CollegeProspectInfo = ({ profileData }) => {
    const dispatch = useDispatch();
    const { isCollegeProspectPowerEnabled } = useCPPDynamicConfig();
    const { hasAnnualPower } = useCollegeProspectAccess();
    const isCppEligibleCoach = useSelector((state) => !!state.auth?.user?.isCollegeCoachCppEligible);

    const subjectMemberId = profileData?.memberId;
    const isSubjectCollegeProspect = !!profileData?.displayCollegeProspectBadge && !!profileData?.isPowered;
    const canViewerRequestCppData = isCppEligibleCoach || hasAnnualPower;
    const shouldShow = isCollegeProspectPowerEnabled &&
        !!subjectMemberId &&
        isSubjectCollegeProspect &&
        canViewerRequestCppData;

    const [statusData, setStatusData] = useState(null);
    const [socialHandles, setSocialHandles] = useState(null);
    const [isViewDenied, setIsViewDenied] = useState(false);

    useEffect(() => {
        if (!shouldShow) {
            return;
        }
        const fetchProspectData = async () => {
            try {
                const [statusResponse, socialResponse] = await Promise.all([
                    getApiAxiosInstance().get(playerRecruitingStatusEndpoint(subjectMemberId)),
                    getApiAxiosInstance().get(playerSocialMediaEndpoint(subjectMemberId)),
                ]);
                setStatusData(statusResponse.data || {});
                setSocialHandles(socialResponse.data || {});
            } catch (err) {
                setIsViewDenied(true);
                if (err?.response?.status !== HTTP_STATUS_FORBIDDEN) {
                    dispatch(showErrorPopup({
                        message: 'We couldn\'t load this player\'s College Prospect info. Please try again.',
                        error: err,
                    }));
                }
            }
        };
        fetchProspectData();
    }, [shouldShow, subjectMemberId, dispatch]);

    if (!shouldShow || isViewDenied || !statusData || !socialHandles) {
        return null;
    }

    const agencyName = statusData.recruitingAgency?.agencyName;
    const agentName = statusData.recruitingAgency?.agentName;
    const agentPhone = statusData.recruitingAgency?.agentPhone;
    const agentEmail = statusData.recruitingAgency?.agentEmail;
    const agentRows = [
        { key: 'name', label: 'Contact Name', value: agentName },
        { key: 'phone', label: 'Phone', value: agentPhone, href: agentPhone && `tel:${agentPhone}` },
        { key: 'email', label: 'Email', value: agentEmail, href: agentEmail && `mailto:${agentEmail}` },
    ];
    const statusLabel = STATUS_LABELS[statusData.statusId] ?? EMPTY_VALUE_TEXT;
    const committedCollegeName = statusData.statusId === CppRecruitingStatuses.COMMITTED
        ? statusData.committedCollegeProgram?.name
        : null;
    const filledPlatforms = SOCIAL_PLATFORMS
        .map((platform) => ({ ...platform, entry: socialHandles[platform.key] }))
        .filter((platform) => platform.entry?.handle);

    return (
        <>
            <div data-testid={`${TESTID_PREFIX}.agency-section`}>
                <h6 className="text-semi mt48" data-testid={`${TESTID_PREFIX}.agency-title`}>
                    College Recruiting Agency
                </h6>
                <div className={styles.infoCard}>
                    <div className={`${styles.cardRow} ${styles.committedRow}`} data-testid={`${TESTID_PREFIX}.agency-row`}>
                        <span className={styles.agencyLabel} data-testid={`${TESTID_PREFIX}.agency-label`}>Agency Name</span>
                        <span className={styles.agencyValue} data-testid={`${TESTID_PREFIX}.agency-value`}>{agencyName || EMPTY_VALUE_TEXT}</span>
                    </div>
                    {isCppEligibleCoach && agentRows.filter((row) => row.value).map((row) => (
                        <div key={row.key} className={`${styles.cardRow} ${styles.committedRow}`} data-testid={`${TESTID_PREFIX}.agent-${row.key}-row`}>
                            <span className={styles.committedLabel} data-testid={`${TESTID_PREFIX}.agent-${row.key}-label`}>{row.label}</span>
                            {row.href ? (
                                <a className={styles.committedValue} href={row.href} data-testid={`${TESTID_PREFIX}.agent-${row.key}-value`}>{row.value}</a>
                            ) : (
                                <span className={styles.committedValue} data-testid={`${TESTID_PREFIX}.agent-${row.key}-value`}>{row.value}</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            {filledPlatforms.length > 0 && (
                <div data-testid={`${TESTID_PREFIX}.social-section`}>
                    <h6 className="text-semi mt48" data-testid={`${TESTID_PREFIX}.social-title`}>
                        Social
                    </h6>
                    <div className={styles.infoCard}>
                        {filledPlatforms.map((platform) => (
                            <div
                                key={platform.key}
                                className={`${styles.cardRow} ${styles.socialRow}`}
                                data-testid={`${TESTID_PREFIX}.social-${platform.key}`}
                            >
                                <span className={styles.socialIconWrapper}>
                                    <Img src={platform.icon} alt={platform.label} />
                                </span>
                                <a
                                    className={styles.socialHandle}
                                    href={platform.entry.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`${TESTID_PREFIX}.social-${platform.key}.link`}
                                >
                                    {platform.entry.handle}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div data-testid={`${TESTID_PREFIX}.status-section`}>
                <h6 className="text-semi mt48" data-testid={`${TESTID_PREFIX}.status-title`}>
                    College Recruitment Status
                </h6>
                <div className={styles.infoCard}>
                    <div className={`${styles.cardRow} ${styles.committedRow}`} data-testid={`${TESTID_PREFIX}.status-row`}>
                        <span className={styles.committedLabel} data-testid={`${TESTID_PREFIX}.status-label`}>
                            Status
                        </span>
                        <span className={styles.committedValue} data-testid={`${TESTID_PREFIX}.status-value`}>
                            {statusLabel}
                        </span>
                    </div>
                    {committedCollegeName && (
                        <div className={`${styles.cardRow} ${styles.committedRow}`} data-testid={`${TESTID_PREFIX}.committed-row`}>
                            <span className={styles.committedLabel} data-testid={`${TESTID_PREFIX}.committed-label`}>
                                Committed To
                            </span>
                            <span className={styles.committedValue} data-testid={`${TESTID_PREFIX}.committed-value`}>
                                {committedCollegeName}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

CollegeProspectInfo.propTypes = {
    profileData: PropTypes.shape({
        memberId: PropTypes.number,
        displayCollegeProspectBadge: PropTypes.bool,
        isPowered: PropTypes.bool,
    }),
};

CollegeProspectInfo.defaultProps = {
    profileData: null,
};

export default CollegeProspectInfo;
